import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useGlobal } from './GlobalProvider'
import { useChannelsData } from './ChannelsDataProvider'
import { useVoiceClient } from 'hooks/voice/useVoiceClient'
import type { VoiceClientState } from 'hooks/voice/types'
import { getCurrentUserV5 } from 'models/api/users'
import { unlockAudio, playBounce } from 'lib/audio/beeper'
import { unlockPcmAudio } from 'lib/audio/OpusStreamPlayer'
import { usePttMutex } from 'hooks/usePTTMutex'
import { Channel } from 'lib/types/channels'
import { ensureMicPermission } from 'lib/audio/permissions'
import { fireWarning } from 'models/PopUpNotifications'
import { addUserToChannel } from 'models/api/channels'
import { isSuperAdmin } from 'lib/utils/auth'
import { canUseVoice } from 'lib/utils/licenses'
import {
  voiceReducer,
  initialVoiceState,
  selectSpeakingChannels,
} from './voiceReducer'

const EVERYONE_CHANNEL_NAME = 'everyone'

function speakTranslation(translations: any, targetLang: string): boolean {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return false
  if (!Array.isArray(translations) || !translations.length) return false

  const lang = String(targetLang || 'en').slice(0, 2).toLowerCase()
  const match = translations.find((t: any) => String(t?.language || '').slice(0, 2).toLowerCase() === lang)
  const text = String(match?.message || '').trim()
  if (!text) return false

  const utter = new SpeechSynthesisUtterance(text)
  utter.lang = match?.language || lang
  utter.volume = 1
  window.speechSynthesis.speak(utter)
  return true
}

const TRANSLATED_RAW_VOLUME = 0.1

function isEveryoneChannelName(name?: string | null): boolean {
  return (
    String(name || '')
      .trim()
      .toLowerCase() === EVERYONE_CHANNEL_NAME
  )
}

function uniqChannelNames(names: Array<string | null | undefined>): string[] {
  const seen = new Set<string>()
  const out: string[] = []

  for (const raw of names) {
    const name = String(raw || '').trim()
    if (!name) continue
    if (seen.has(name)) continue
    seen.add(name)
    out.push(name)
  }

  return out
}

export function normalizeChannelName(c: any): string | null {
  return c?.name ?? c?.channelName ?? c?.displayName ?? c?.title ?? null
}

export function normalizeChannelId(c: any): string | null {
  const idLike = c?.id ?? c?.channelId ?? c?.channel_id ?? normalizeChannelName(c)
  return idLike != null ? String(idLike) : null
}

type VoicePrefs = {
  listeningChannelNames: string[]
  selectedTxChannelName: string | null
}

function getVoicePrefsKey(userId?: string, orgId?: string | null) {
  if (!userId || !orgId) return null
  return `voice:prefs:${userId}:${orgId}`
}

function readVoicePrefs(userId?: string, orgId?: string | null): VoicePrefs {
  const key = getVoicePrefsKey(userId, orgId)
  if (!key) {
    return {
      listeningChannelNames: [],
      selectedTxChannelName: null,
    }
  }

  try {
    const raw = localStorage.getItem(key)
    if (!raw) {
      return {
        listeningChannelNames: [],
        selectedTxChannelName: null,
      }
    }

    const parsed = JSON.parse(raw)
    return {
      listeningChannelNames: Array.isArray(parsed?.listeningChannelNames)
        ? parsed.listeningChannelNames.map((v: any) => String(v)).filter(Boolean)
        : [],
      selectedTxChannelName: parsed?.selectedTxChannelName ? String(parsed.selectedTxChannelName) : null,
    }
  } catch {
    return {
      listeningChannelNames: [],
      selectedTxChannelName: null,
    }
  }
}

function writeVoicePrefs(userId?: string, orgId?: string | null, prefs?: VoicePrefs) {
  const key = getVoicePrefsKey(userId, orgId)
  if (!key || !prefs) return

  try {
    localStorage.setItem(key, JSON.stringify(prefs))
  } catch {
    /* noop */
  }
}

type VoiceContextType = {
  // The active backend's session state (named before fleet existed).
  zelloState: VoiceClientState
  currentChannelId: string | null
  currentChannelName: string | null
  joiningChannelId: string | null
  isConnected: boolean
  isKicked: boolean
  voiceReady: boolean
  channelBusy: boolean
  canPTT: boolean
  availableChannels: Channel[]
  setAvailableChannels: (chs: Channel[]) => void

  listeningChannelNames: string[]
  isListening: (c: Channel) => boolean
  toggleListening: (c: Channel) => Promise<void>

  isReceiving: (c: Channel) => boolean
  receivingChannelNames: string[]

  setTxChannel: (c: Channel | null) => void

  reconnect: () => void

  connectToChannel: (c: Channel) => Promise<void>
  leaveChannel: (c?: Channel) => void

  enterExclusiveChannel: (c: Channel) => Promise<void>
  exitExclusiveChannel: () => Promise<void>
  exclusiveChannelId: string | null
  exclusiveChannelName: string | null

  pttStart: () => void
  pttStop: () => void

  // True once the backend reports the channel can be talked on.
  // Used to show a loader on PTT controls until the channel is ready.
  isChannelReady: (channelName: string | null | undefined) => boolean

  // Per-channel state synthesized from the shared session — useful for
  // channel-row UIs that want fine-grained status without leaking aggregate.
  getSessionState: (channelName: string | null | undefined) => VoiceClientState | null
}

const VoiceContext = createContext<VoiceContextType | null>(null)

export const useVoice = () => {
  const ctx = useContext(VoiceContext)
  if (!ctx) throw new Error('useVoice must be used within VoiceProvider')
  return ctx
}

export function VoiceProvider({ children }: { children: ReactNode }) {
  const { selectedOrg, selectedOrgId, currentUserV5, setCurrentUserV5, licenses } = useGlobal()

  const blockVoiceActions = !canUseVoice({
    userId: currentUserV5?.id,
    orgId: selectedOrgId,
    licenses,
    superAdmin: isSuperAdmin(),
  })

  const { i18n } = useTranslation()
  const zelloLanguage = (i18n.language || 'en').slice(0, 2)

  // The org's voice backend (Zello or React Mobile's fleet) behind one
  // interface; see hooks/voice/types.ts.
  const refetchCurrentUser = useCallback(() => getCurrentUserV5().then(setCurrentUserV5), [setCurrentUserV5])
  const client = useVoiceClient({
    org: selectedOrg ?? null,
    orgId: selectedOrgId ? String(selectedOrgId) : null,
    currentUser: currentUserV5,
    language: zelloLanguage,
    refetchCurrentUser,
  })

  // ===== FSM-authoritative state =====
  // See voice-fsm.md (repo root) for the full design.
  const [fsmState, dispatch] = useReducer(voiceReducer, initialVoiceState)

  // UI-only state that doesn't belong in the FSM (transient spinner id).
  const [joiningChannelId, setJoiningChannelId] = useState<string | null>(null)

  // Derived from FSM — these replace the legacy useStates.
  const listeningChannelNames = fsmState.prefs.listening
  const currentChannelName = fsmState.prefs.selectedTx
  const exclusiveChannelName = fsmState.exclusive.channel
  const prefsHydrated = fsmState.prefs.status === 'hydrated'

  // Channels data — owned by ChannelsDataProvider.
  const channelsData = useChannelsData()
  const availableChannels = channelsData.channels
  const setAvailableChannels = channelsData.setChannels
  const channelsLoaded =
    channelsData.channelsLoaded && channelsData.channelsOrgId === selectedOrgId

  // Name → Channel lookup (for id resolution).
  const channelByName = useMemo(() => {
    const map = new Map<string, Channel>()
    for (const c of availableChannels) {
      const name = normalizeChannelName(c)
      if (name) map.set(name.toLowerCase(), c)
    }
    return map
  }, [availableChannels])

  const lookupChannelId = useCallback(
    (name: string | null): string | null => {
      if (!name) return null
      const c = channelByName.get(name.toLowerCase())
      return c ? normalizeChannelId(c) || null : null
    },
    [channelByName],
  )

  const currentChannelId = useMemo(() => lookupChannelId(currentChannelName), [
    lookupChannelId,
    currentChannelName,
  ])
  const exclusiveChannelId = useMemo(() => lookupChannelId(exclusiveChannelName), [
    lookupChannelId,
    exclusiveChannelName,
  ])

  const receivingChannelNames = useMemo(
    () => selectSpeakingChannels(fsmState),
    [fsmState],
  )

  const mutex = usePttMutex(exclusiveChannelName || currentChannelName || undefined)

  const orgReady = Boolean(selectedOrgId && selectedOrg && String(selectedOrg.id) === String(selectedOrgId))
  const voiceReady = Boolean(orgReady && client.ready)

  const prefsKey = useMemo(() => {
    return getVoicePrefsKey(currentUserV5?.id, selectedOrgId || null)
  }, [currentUserV5?.id, selectedOrgId])

  const contextKey = useMemo(() => {
    return `${String(currentUserV5?.id || '')}::${String(selectedOrgId || '')}`
  }, [currentUserV5?.id, selectedOrgId])

  const prevContextKeyRef = useRef<string | null>(null)
  const lastBusyErrorRef = useRef<string | null>(null)

  const effectiveTxChannelName = exclusiveChannelName || currentChannelName || null

  const channelBusy = useMemo(() => {
    return String(client.state.error || '')
      .toLowerCase()
      .includes('busy')
  }, [client.state.error])

  const allChannelNames = useMemo(() => {
    const list = Array.isArray(availableChannels) ? availableChannels : []
    return list.map(c => normalizeChannelName(c)).filter((v): v is string => Boolean(v))
  }, [availableChannels])

  const everyoneChannelName = useMemo(() => {
    return allChannelNames.find(name => isEveryoneChannelName(name)) || null
  }, [allChannelNames])

  const allChannelsKey = useMemo(() => {
    return [...allChannelNames].sort().join('|')
  }, [allChannelNames])

  const listeningChannelsKey = useMemo(() => {
    return [...listeningChannelNames].sort().join('|')
  }, [listeningChannelNames])

  // ===== Mirror effects: bridge non-FSM sources into the FSM =====
  // The voice client keeps its own internal state — these mirror it
  // into the FSM so consumers can read a single source of truth.

  // Identity → IDENTITY_RESOLVED resets all downstream slices on change.
  useEffect(() => {
    if (currentUserV5?.id && selectedOrgId) {
      dispatch({
        type: 'IDENTITY_RESOLVED',
        userId: String(currentUserV5.id),
        orgId: String(selectedOrgId),
      })
    } else {
      dispatch({ type: 'IDENTITY_CLEARED' })
    }
  }, [currentUserV5?.id, selectedOrgId])

  // ChannelsData
  useEffect(() => {
    if (channelsData.loadingChannels && selectedOrgId) {
      dispatch({ type: 'CHANNELS_FETCH_STARTED', fetchOrgId: String(selectedOrgId) })
    }
  }, [channelsData.loadingChannels, selectedOrgId])

  useEffect(() => {
    if (channelsLoaded && channelsData.channelsOrgId) {
      dispatch({
        type: 'CHANNELS_FETCH_OK',
        orgId: String(channelsData.channelsOrgId),
        channels: availableChannels,
      })
    }
  }, [channelsLoaded, channelsData.channelsOrgId, availableChannels])

  // WS status (excluding 'connecting' — the connect effect dispatches that
  // directly with the correct channels payload).
  useEffect(() => {
    switch (client.state.status) {
      case 'connected':
        dispatch({ type: 'WS_LOGON_OK', channels: fsmState.ws.desired })
        break
      case 'kicked':
        dispatch({ type: 'WS_KICKED' })
        break
      case 'lost':
      case 'disconnected':
        dispatch({ type: 'WS_CLOSED', code: 0, reason: client.state.status })
        break
      case 'failed':
        dispatch({ type: 'WS_WATCHDOG_FIRED' })
        break
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.state.status])

  useEffect(() => {
    if (client.state.connectionFailed) dispatch({ type: 'WS_WATCHDOG_FIRED' })
  }, [client.state.connectionFailed])

  // readyChannels diff
  const lastReadyKeyRef = useRef('')
  useEffect(() => {
    const key = [...client.state.readyChannels].sort().join('|')
    if (key === lastReadyKeyRef.current) return
    lastReadyKeyRef.current = key
    const next = new Set(client.state.readyChannels.map(c => c.toLowerCase()))
    const prev = new Set(fsmState.ws.readyChannels.map(c => c.toLowerCase()))
    next.forEach(c => {
      if (!prev.has(c)) dispatch({ type: 'WS_CHANNEL_STATUS', channel: c, status: 'online' })
    })
    prev.forEach(c => {
      if (!next.has(c)) dispatch({ type: 'WS_CHANNEL_STATUS', channel: c, status: 'offline' })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.state.readyChannels])

  // TX status from client
  useEffect(() => {
    if (client.state.tx === 'sending' && fsmState.tx.status !== 'sending') {
      dispatch({ type: 'TX_START_STREAM_OK', streamId: -1 })
    } else if (client.state.tx === 'idle' && fsmState.tx.status === 'sending') {
      dispatch({ type: 'TX_STOPPED' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.state.tx])

  // Server-busy flash
  useEffect(() => {
    if (channelBusy) dispatch({ type: 'TX_START_STREAM_BUSY' })
  }, [channelBusy])

  // Active TX channel (kept in sync for selectors).
  useEffect(() => {
    dispatch({ type: 'TX_CHANNEL_CHANGED', channel: effectiveTxChannelName })
  }, [effectiveTxChannelName])

  // Dev-only: expose the FSM for browser-devtools inspection.
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return
    ;(window as any).__voiceFsm = { state: fsmState, dispatch }
  }, [fsmState])

  // ===== Side-effect bridges =====

  useEffect(() => {
    client.setActiveChannel(effectiveTxChannelName)
  }, [effectiveTxChannelName, client.setActiveChannel])

  // contextKey change — perform the imperative teardown. State reset is
  // handled by IDENTITY_RESOLVED firing in the identity mirror effect above.
  useEffect(() => {
    const isValid = !!currentUserV5?.id && !!selectedOrgId
    if (!isValid) return

    const prev = prevContextKeyRef.current
    prevContextKeyRef.current = contextKey

    if (prev === null) return
    if (prev === contextKey) return

    const run = async () => {
      try {
        await client.stopTalking(effectiveTxChannelName || undefined)
      } catch (err) {
        console.log(err)
      }
      mutex.release()
      await client.disconnectAll()
      lastBusyErrorRef.current = null
    }

    void run()
  }, [contextKey, effectiveTxChannelName, mutex, client])

  useEffect(() => {
    const err = String(client.state.error || '').toLowerCase()
    if (!err.includes('busy')) return
    if (lastBusyErrorRef.current === err) return

    lastBusyErrorRef.current = err
    void playBounce()
  }, [client.state.error])

  useEffect(() => {
    const err = String(client.state.error || '').toLowerCase()
    if (!err.includes('busy')) {
      lastBusyErrorRef.current = null
    }
  }, [client.state.error])

  // ===== Transport events → FSM dispatches + side-effects =====
  useEffect(() => {
    const handleEvent = (e: any) => {
      if (e?.type === 'stream_start') {
        const channelName = String(e.channel || '').trim()
        if (!channelName) return

        dispatch({
          type: 'RX_STREAM_STARTED',
          streamId: Number(e.stream_id) || 0,
          channel: channelName,
          from: e.from ? String(e.from) : null,
          codec: null,
          language: e.language ? String(e.language) : null,
        })

        if (e.translationsEnabled && typeof e.stream_id === 'number') {
          const senderLang = String(e.language || '').slice(0, 2).toLowerCase()
          const listenerLang = String(zelloLanguage || '').slice(0, 2).toLowerCase()
          if (senderLang && listenerLang && senderLang !== listenerLang) {
            client.setStreamVolume(e.stream_id, TRANSLATED_RAW_VOLUME)
          }
        }
        return
      }

      if (e?.type === 'stream_stop') {
        if (typeof e.stream_id === 'number') {
          dispatch({ type: 'RX_STREAM_STOPPED', streamId: e.stream_id })
        }
        return
      }

      if (e?.type === 'channel_not_found') {
        const channelName = String(e.channel || '').trim()
        if (!channelName) return

        // Transport already dropped the channel; mirror into the FSM so
        // the connect effect doesn't immediately re-add it.
        dispatch({ type: 'WS_CHANNEL_NOT_FOUND', channel: channelName })
        dispatch({ type: 'PREFS_USER_LEFT_CHANNEL', channel: channelName })
        if (exclusiveChannelName && exclusiveChannelName.toLowerCase() === channelName.toLowerCase()) {
          dispatch({ type: 'EXCLUSIVE_CHANNEL_NOT_FOUND', channel: channelName })
        }
        return
      }

      if (e?.type === 'transcription') {
        const channelName = e.channel ? String(e.channel) : ''
        if (channelName && typeof e.stream_id === 'number') {
          dispatch({
            type: 'TRANSCRIPTION_FINAL',
            streamId: e.stream_id,
            channel: channelName,
            text: String(e.text || ''),
            lang: e.language ? String(e.language) : null,
          })
        }
        speakTranslation(e.translations, zelloLanguage)
        return
      }
    }

    client.setOnEvent(handleEvent)
    return () => {
      client.setOnEvent(null)
    }
  }, [client, zelloLanguage, exclusiveChannelName])

  // ===== Prefs hydration =====
  // Reset prefs status when org goes away so the next valid org re-hydrates.
  useEffect(() => {
    if (!selectedOrgId || !orgReady) {
      dispatch({ type: 'IDENTITY_CLEARED' })
    }
  }, [selectedOrgId, orgReady])

  useEffect(() => {
    if (!orgReady) return
    if (!channelsLoaded) return
    if (prefsHydrated) return

    // Belt-and-suspenders: if the channels list is empty, bail BEFORE
    // dispatching PREFS_HYDRATED. Otherwise persistPrefs would later save
    // empty lists over the user's saved selections.
    if (allChannelNames.length === 0) return

    const availableNameSet = new Set(allChannelNames)
    const everyoneAvailableName = allChannelNames.find(name => isEveryoneChannelName(name)) || null

    if (!prefsKey) {
      const baseListening = everyoneAvailableName ? [everyoneAvailableName] : []
      client.setRxEnabledChannels(baseListening)
      dispatch({ type: 'PREFS_HYDRATED', listening: baseListening, selectedTx: null })
      return
    }

    const prefs = readVoicePrefs(currentUserV5?.id, selectedOrgId || null)

    const restoredListening = uniqChannelNames([
      ...(everyoneAvailableName ? [everyoneAvailableName] : []),
      ...prefs.listeningChannelNames.filter(name => availableNameSet.has(name)),
    ])

    const restoredTxName =
      prefs.selectedTxChannelName &&
      availableNameSet.has(prefs.selectedTxChannelName) &&
      restoredListening.includes(prefs.selectedTxChannelName)
        ? prefs.selectedTxChannelName
        : null

    client.setRxEnabledChannels(restoredListening)
    if (!restoredTxName && !exclusiveChannelName) {
      client.setActiveChannel(null)
    }

    dispatch({ type: 'PREFS_HYDRATED', listening: restoredListening, selectedTx: restoredTxName })
  }, [
    orgReady,
    prefsKey,
    channelsLoaded,
    prefsHydrated,
    allChannelsKey,
    allChannelNames,
    currentUserV5?.id,
    selectedOrgId,
    exclusiveChannelName,
    client.setActiveChannel,
    client.setRxEnabledChannels,
  ])

  // ===== Prefs persistence (explicit user actions only) =====
  // Connection state — re-logons, channel-not-found filtering, hydration
  // races, contextKey resets, alert exclusive mode, etc. — never touches the
  // saved prefs. This decouples "what the user wants to listen to" from
  // "what we happen to be subscribed to right now."
  const persistPrefs = useCallback(
    (nextListening: string[], nextTx: string | null) => {
      if (!currentUserV5?.id || !selectedOrgId) return
      writeVoicePrefs(currentUserV5.id, selectedOrgId, {
        listeningChannelNames: nextListening.filter(name => !isEveryoneChannelName(name)),
        selectedTxChannelName: nextTx,
      })
    },
    [currentUserV5?.id, selectedOrgId],
  )

  const reconnect = useCallback(() => {
    // The transport records the failure in state and schedules its own retry.
    client.forceReconnect().catch(err => console.warn('[Voice] reconnect failed', err))
  }, [client.forceReconnect])

  const prevLangRef = useRef<string>(zelloLanguage)
  useEffect(() => {
    if (prevLangRef.current === zelloLanguage) return
    prevLangRef.current = zelloLanguage
    if (client.state.status === 'connected' || client.state.status === 'connecting') {
      void client.disconnect()
    }
  }, [zelloLanguage, client.state.status, client.disconnect])

  // Track kicked-state via a ref so the connect effect can read it without
  // depending on client.state.status (which would refire on every ws
  // transition and race the transport's auto-reconnect).
  const kickedRef = useRef(false)
  useEffect(() => {
    kickedRef.current = client.state.status === 'kicked'
  }, [client.state.status])

  // ===== Connect effect =====
  useEffect(() => {
    if (!orgReady || !prefsHydrated) return
    if (kickedRef.current) return

    // A super admin without a license here only views channels; the backend
    // refuses them, so no session is opened.
    if (!voiceReady || (isSuperAdmin() && blockVoiceActions)) {
      void client.disconnectAll()
      return
    }

    // In exclusive mode subscribe ONLY to the alert channel. Otherwise:
    // Everyone + listening + TX (TX explicitly as safety net).
    const connectNames = exclusiveChannelName
      ? uniqChannelNames([exclusiveChannelName])
      : uniqChannelNames([everyoneChannelName, ...listeningChannelNames, currentChannelName])

    client.setRxEnabledChannels(connectNames)

    if (!connectNames.length) {
      void client.disconnectAll()
      return
    }

    // If we're already logged on to exactly this set, no reconnect is
    // needed — selecting an already-subscribed channel for TX just needs
    // setActiveChannel (handled by a sibling effect). Without this guard
    // we'd flip ws.status → 'connecting' but client.connect would
    // short-circuit internally, leaving the FSM stuck because
    // client.state.status never transitions.
    const desiredKey = [...connectNames].map(c => c.toLowerCase()).sort().join('|')
    const loggedOnKey = [...fsmState.ws.loggedOnChannels].map(c => c.toLowerCase()).sort().join('|')
    if (client.state.status === 'connected' && desiredKey === loggedOnKey) return

    // A shared session already carries every membership; a new listening set
    // only changes what plays, so it is just re-recorded as logged on
    // (WS_CONNECT_REQUESTED would clear readyChannels it won't resend).
    if (client.capabilities.sharedSession && client.state.status === 'connected') {
      void client.connect(connectNames)
      dispatch({ type: 'WS_LOGON_OK', channels: connectNames })
      return
    }

    dispatch({ type: 'WS_CONNECT_REQUESTED', channels: connectNames })
    // The transport records the failure in state and schedules its own retry.
    client.connect(connectNames).catch(err => console.warn('[Voice] connect failed', err))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    orgReady,
    prefsHydrated,
    voiceReady,
    client.capabilities.sharedSession,
    blockVoiceActions,
    everyoneChannelName,
    exclusiveChannelName,
    listeningChannelsKey,
    currentChannelName,
    client.connect,
    client.disconnectAll,
    client.setRxEnabledChannels,
  ])

  const isListening = useCallback(
    (c: Channel) => {
      const name = normalizeChannelName(c)
      if (!name) return false
      return listeningChannelNames.includes(name)
    },
    [listeningChannelNames],
  )

  const isReceiving = useCallback(
    (c: Channel) => {
      const name = normalizeChannelName(c)
      if (!name) return false
      const key = name.toLowerCase()
      return Object.values(fsmState.rx.streamsById).some(s => s.channel.toLowerCase() === key)
    },
    [fsmState.rx.streamsById],
  )

  const toggleListening = useCallback(
    async (c: Channel) => {
      if (blockVoiceActions) return
      const channelName = normalizeChannelName(c)
      const channelId = normalizeChannelId(c) || null
      if (!channelName) return
      if (isEveryoneChannelName(channelName)) return

      setJoiningChannelId(channelId)

      try {
        const orgId = selectedOrgId || localStorage.getItem('selected_org_id') || undefined
        const currentlyListening = listeningChannelNames.includes(channelName)
        const nextListening = !currentlyListening

        if (nextListening && channelName && currentUserV5?.id && orgId) {
          try {
            await addUserToChannel(currentUserV5.id, channelName, orgId)
          } catch (e) {
            console.error(e)
          }
        }

        if (!exclusiveChannelName) {
          client.setChannelRxEnabled(channelName, nextListening)
        }

        // Compute next prefs explicitly for persistPrefs (dispatch is async).
        const nextListeningArr = nextListening
          ? uniqChannelNames([...listeningChannelNames, channelName])
          : listeningChannelNames.filter(n => n !== channelName)

        let nextTx: string | null = currentChannelName
        if (!nextListening && currentChannelName === channelName) {
          nextTx = null
        }

        dispatch({ type: 'PREFS_USER_TOGGLED_LISTENING', channel: channelName, on: nextListening })
        if (nextTx !== currentChannelName) {
          dispatch({ type: 'PREFS_USER_SET_TX', channel: null })
          if (!exclusiveChannelName) {
            client.setActiveChannel(null)
          }
        }

        persistPrefs(nextListeningArr, nextTx)
      } finally {
        setJoiningChannelId(null)
      }
    },
    [
      blockVoiceActions,
      currentChannelName,
      currentUserV5?.id,
      selectedOrgId,
      listeningChannelNames,
      exclusiveChannelName,
      persistPrefs,
      client.setActiveChannel,
      client.setChannelRxEnabled,
    ],
  )

  const connectToChannel = useCallback(
    async (c: Channel) => {
      const name = normalizeChannelName(c)
      const idStr = normalizeChannelId(c) || null
      if (!name) return

      setJoiningChannelId(idStr)
      try {
        if (!exclusiveChannelName) {
          client.setChannelRxEnabled(name, true)
        }
        dispatch({ type: 'PREFS_USER_TOGGLED_LISTENING', channel: name, on: true })
      } finally {
        setJoiningChannelId(null)
      }
    },
    [exclusiveChannelName, client.setChannelRxEnabled],
  )

  const leaveChannel = useCallback(
    (c?: Channel) => {
      if (!c) {
        // "No channel" — clear TX selection only.
        dispatch({ type: 'PREFS_USER_SET_TX', channel: null })
        if (!exclusiveChannelName) {
          client.setActiveChannel(null)
        }
        persistPrefs(listeningChannelNames, null)
        return
      }

      const name = normalizeChannelName(c)
      if (!name) return
      if (isEveryoneChannelName(name)) return

      if (!exclusiveChannelName) {
        client.setChannelRxEnabled(name, false)
      }

      const nextListeningArr = listeningChannelNames.filter(n => n !== name)
      let nextTx: string | null = currentChannelName
      if (currentChannelName === name) {
        nextTx = null
        if (!exclusiveChannelName) {
          client.setActiveChannel(null)
        }
      }

      dispatch({ type: 'PREFS_USER_LEFT_CHANNEL', channel: name })
      persistPrefs(nextListeningArr, nextTx)
    },
    [
      currentChannelName,
      exclusiveChannelName,
      listeningChannelNames,
      persistPrefs,
      client.setActiveChannel,
      client.setChannelRxEnabled,
    ],
  )

  const setTxChannel = useCallback(
    (c: Channel | null) => {
      if (!c) {
        dispatch({ type: 'PREFS_USER_SET_TX', channel: null })
        if (!exclusiveChannelName) {
          client.setActiveChannel(null)
        }
        persistPrefs(listeningChannelNames, null)
        return
      }

      const name = normalizeChannelName(c)
      if (!name) return

      if (!exclusiveChannelName) {
        client.setActiveChannel(name)
        client.setChannelRxEnabled(name, true)
      }

      const nextListeningArr = uniqChannelNames([...listeningChannelNames, name])
      dispatch({ type: 'PREFS_USER_TOGGLED_LISTENING', channel: name, on: true })
      dispatch({ type: 'PREFS_USER_SET_TX', channel: name })
      persistPrefs(nextListeningArr, name)
    },
    [
      exclusiveChannelName,
      listeningChannelNames,
      persistPrefs,
      client.setActiveChannel,
      client.setChannelRxEnabled,
    ],
  )

  const enterExclusiveChannel = useCallback(
    async (c: Channel) => {
      const name = normalizeChannelName(c)
      if (!name) return

      const orgId = selectedOrgId || localStorage.getItem('selected_org_id') || undefined
      if (name && currentUserV5?.id && orgId) {
        try {
          await addUserToChannel(currentUserV5.id, name, orgId)
        } catch (e) {
          console.error(e)
        }
      }

      dispatch({ type: 'EXCLUSIVE_ENTERED', channel: name })
      client.setActiveChannel(name)
    },
    [currentUserV5?.id, selectedOrgId, client.setActiveChannel],
  )

  const exitExclusiveChannel = useCallback(async () => {
    try {
      await client.stopTalking(exclusiveChannelName || undefined)
    } catch (err) {
      console.log(err)
    }

    mutex.release()
    dispatch({ type: 'EXCLUSIVE_EXITED' })
    client.setActiveChannel(currentChannelName || null)
  }, [exclusiveChannelName, currentChannelName, mutex, client])

  const pttStart = useCallback(async () => {
    if (blockVoiceActions) return
    if (!effectiveTxChannelName) return

    void unlockAudio()
    void unlockPcmAudio()

    const t0 = Date.now()
    const mic = await ensureMicPermission()
    if (!mic.ok) return

    const elapsed = Date.now() - t0
    const PROMPT_THRESHOLD_MS = 600
    if (elapsed > PROMPT_THRESHOLD_MS) {
      fireWarning({ content: 'Microphone enabled — press the talk button again to start.' })
      return
    }
    if (!mutex.acquire()) return

    dispatch({ type: 'TX_REQUESTED', channel: effectiveTxChannelName })
    await client.startTalking(effectiveTxChannelName)
  }, [blockVoiceActions, effectiveTxChannelName, mutex, client])

  const pttStop = useCallback(() => {
    if (!effectiveTxChannelName) return
    void client.stopTalking(effectiveTxChannelName)
    mutex.release()
  }, [effectiveTxChannelName, mutex, client])

  const isChannelReady = useCallback(
    (channelName: string | null | undefined) => {
      if (!channelName) return false
      const key = String(channelName).trim().toLowerCase()
      if (!key) return false
      return fsmState.ws.readyChannels.some(c => c.toLowerCase() === key)
    },
    [fsmState.ws.readyChannels],
  )

  // Synthesize a per-channel state from the shared session. Status/tx/rx are
  // shared (one WS), but `readyChannels` is per-channel and the channel is
  // only "valid" if it's in the listening set.
  const getSessionState = useCallback(
    (channelName: string | null | undefined): VoiceClientState | null => {
      if (!channelName) return null
      const name = String(channelName).trim()
      if (!name) return null
      if (!listeningChannelNames.includes(name)) return null
      return client.state
    },
    [client.state, listeningChannelNames],
  )

  // Global backtick (`) PTT hotkey. Hold to talk, release to stop.
  const hotkeyHeldRef = useRef(false)
  useEffect(() => {
    const isTypingTarget = (t: EventTarget | null): boolean => {
      const el = t as HTMLElement | null
      if (!el) return false
      const tag = el.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
      if ((el as HTMLElement).isContentEditable) return true
      return false
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== '`') return
      if (e.repeat) return
      if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return
      if (isTypingTarget(e.target)) return
      if (hotkeyHeldRef.current) return

      hotkeyHeldRef.current = true
      e.preventDefault()
      void pttStart()
    }

    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key !== '`') return
      if (!hotkeyHeldRef.current) return

      hotkeyHeldRef.current = false
      e.preventDefault()
      pttStop()
    }

    const releaseOnBlur = () => {
      if (!hotkeyHeldRef.current) return
      hotkeyHeldRef.current = false
      pttStop()
    }

    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    window.addEventListener('blur', releaseOnBlur)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
      window.removeEventListener('blur', releaseOnBlur)
    }
  }, [pttStart, pttStop])

  const value = useMemo<VoiceContextType>(
    () => ({
      zelloState: client.state,
      currentChannelId,
      currentChannelName,
      joiningChannelId,

      isConnected: client.state.status === 'connected',
      isKicked: client.state.status === 'kicked',
      voiceReady,
      channelBusy,
      canPTT: client.canPTT,

      availableChannels,
      setAvailableChannels,

      listeningChannelNames,
      isListening,
      toggleListening,

      isReceiving,
      receivingChannelNames,

      setTxChannel,

      reconnect,

      connectToChannel,
      leaveChannel,

      enterExclusiveChannel,
      exitExclusiveChannel,
      exclusiveChannelId,
      exclusiveChannelName,

      pttStart,
      pttStop,

      isChannelReady,
      getSessionState,
    }),
    [
      client.state,
      client.canPTT,
      isChannelReady,
      getSessionState,
      currentChannelId,
      currentChannelName,
      joiningChannelId,
      voiceReady,
      channelBusy,
      availableChannels,
      setAvailableChannels,
      listeningChannelNames,
      isListening,
      toggleListening,
      isReceiving,
      receivingChannelNames,
      setTxChannel,
      reconnect,
      connectToChannel,
      leaveChannel,
      enterExclusiveChannel,
      exitExclusiveChannel,
      exclusiveChannelId,
      exclusiveChannelName,
      pttStart,
      pttStop,
    ],
  )

  return <VoiceContext.Provider value={value}>{children}</VoiceContext.Provider>
}
